import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, Pipeline, PipelineExecution, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService, MonitoringFinding } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { ActivityItem, findingsToActivity, pipelineRunsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'executions' | 'activity';

const DEFAULT_SORT = 'last-exec-desc';
const FACET_KEYS   = ['status', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'last-exec-desc',  label: 'Most recent'    },
  { value: 'name-asc',        label: 'Name A → Z'     },
  { value: 'name-desc',       label: 'Name Z → A'     },
  { value: 'status-asc',      label: 'Status'         },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'Succeeded',  label: 'Succeeded'   },
      { value: 'Failed',     label: 'Failed'      },
      { value: 'InProgress', label: 'In Progress' },
      { value: 'Stopped',    label: 'Stopped'     },
      { value: 'Unknown',    label: 'Unknown'     },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

const STATUS_ORDER: Record<string, number> = {
  Failed: 0, InProgress: 1, Stopped: 2, Unknown: 3, Succeeded: 4,
};

@Component({
  selector: 'app-codepipeline-detail',
  templateUrl: './codepipeline-detail.component.html',
})
export class CodePipelineDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  pipelines: Pipeline[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',   label: 'Overview'   },
    { id: 'executions', label: 'Pipelines'  },
    { id: 'activity',   label: 'Activity'   },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'codepipeline', name: 'CodePipeline', icon: 'git-branch', route: '/cloud/codepipeline', status: 'healthy', metrics: [] };

  selectedPipeline: string | null = null;
  drawerItemId: string | null = null;
  executionHistory: PipelineExecution[] = [];
  historyLoading = false;
  historyError: string | null = null;
  historyPage = 1;
  readonly historyPageSize = 10;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;
  private allFindings: MonitoringFinding[] = [];

  private sub?: Subscription;
  private routeSub?: Subscription;
  private historySub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('codepipeline').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      this.filters = parseFiltersFromParams(params as Record<string, string>, DEFAULT_SORT, FACET_KEYS);
      const pipelineName = (params['pipeline'] as string | undefined) ?? null;
      if (pipelineName !== this.selectedPipeline) {
        this.selectedPipeline = pipelineName;
        this.historyPage = 1;
        if (pipelineName) {
          this.loadExecutionHistory();
        } else {
          this.executionHistory = [];
          this.historySub?.unsubscribe();
        }
      }
    });
    this.loadData(false);
  }

  /** Loads the pipeline list (cache-aware), monitoring state (monitored count), and rebuilds activity. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getPipelines(bypass).subscribe(pipelines => {
      this.pipelines  = pipelines;
      this.loading    = false;
      this.refreshing = false;
      this.rebuildActivity();
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.allFindings     = findings;
      this.activityLoading = false;
      this.rebuildActivity();
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.historySub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  // ── Activity feed ───────────────────────────────────────────────────────────

  private rebuildActivity(): void {
    this.activity = sortActivity([
      ...findingsToActivity(this.allFindings, 'PIPELINE'),
      ...pipelineRunsToActivity(this.pipelines),
    ]);
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'executions';
    this.selectPipeline(item.resourceId);
  }

  // ── Summary counts (standardized: total + monitored) ────────────────────────

  get totalCount():     number { return this.pipelines.length; }
  get monitoredCount(): number { return this.pipelines.filter(p => this.cloud.isMonitored('codepipeline', p.name)).length; }

  // ── Overview counts (unfiltered) ────────────────────────────────────────────

  get succeededCount():  number { return this.pipelines.filter(p => p.status === 'Succeeded').length; }
  get failedCount():     number { return this.pipelines.filter(p => p.status === 'Failed').length; }
  get inProgressCount(): number { return this.pipelines.filter(p => p.status === 'InProgress').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredPipelines(): Pipeline[] {
    let result = this.pipelines;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(p => p.name.toLowerCase().includes(q));

    const statuses = this.filters.facets['status'] ?? [];
    if (statuses.length) result = result.filter(p => statuses.includes(p.status));

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(p => {
        const mon = this.cloud.isMonitored('codepipeline', p.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(pipes: Pipeline[], sort: string): Pipeline[] {
    return [...pipes].sort((a, b) => {
      switch (sort) {
        case 'name-asc':      return a.name.localeCompare(b.name);
        case 'name-desc':     return b.name.localeCompare(a.name);
        case 'status-asc':    return (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
        case 'monitored-first': {
          const aM = this.cloud.isMonitored('codepipeline', a.name) ? 0 : 1;
          const bM = this.cloud.isMonitored('codepipeline', b.name) ? 0 : 1;
          return aM - bM;
        }
        default:              return (b.lastExecutionAt ?? '').localeCompare(a.lastExecutionAt ?? '');
      }
    });
  }

  selectPipeline(name: string): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { pipeline: name },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  closePipeline(): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         { pipeline: null },
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  private loadExecutionHistory(): void {
    if (!this.selectedPipeline) return;
    this.historyLoading = true;
    this.historyError   = null;
    this.historySub?.unsubscribe();
    this.historySub = this.cloud.getPipelineExecutions(this.selectedPipeline).subscribe({
      next: (executions) => {
        this.executionHistory = executions;
        this.historyLoading   = false;
      },
      error: () => {
        this.historyError   = 'Failed to load execution history.';
        this.historyLoading = false;
      },
    });
  }

  get historyTotalPages(): number {
    return Math.max(1, Math.ceil(this.executionHistory.length / this.historyPageSize));
  }

  get pagedExecutionHistory(): PipelineExecution[] {
    const start = (this.historyPage - 1) * this.historyPageSize;
    return this.executionHistory.slice(start, start + this.historyPageSize);
  }

  historyStatusClass(status: string): string {
    const map: Record<string, string> = {
      Succeeded:  'bg-emerald-100 text-emerald-700',
      Failed:     'bg-red-100 text-red-700',
      InProgress: 'bg-blue-100 text-blue-700',
      Stopped:    'bg-slate-100 text-slate-500',
      Stopping:   'bg-slate-100 text-slate-500',
      Superseded: 'bg-slate-100 text-slate-400',
      Cancelled:  'bg-slate-100 text-slate-400',
    };
    return map[status] ?? 'bg-slate-100 text-slate-400';
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring quota ────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(name: string): boolean { return this.cloud.isMonitored('codepipeline', name); }

  toggleMonitoring(name: string): void {
    const current = this.cloud.isMonitored('codepipeline', name);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('codepipeline', name, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Item settings drawer ────────────────────────────────────────────────��───

  openItemSettings(name: string): void  { this.drawerItemId = name; }
  closeItemSettings(): void             { this.drawerItemId = null; }

  itemOverrideSummary(name: string): string {
    return this.cloud.getItemOverrideSummary('codepipeline', name);
  }

  // ── Status badge ────────────────────────────────────────────────────────────

  statusClass(status: Pipeline['status']): string {
    const map: Record<string, string> = {
      Succeeded:  'bg-emerald-100 text-emerald-700',
      Failed:     'bg-red-100 text-red-700',
      InProgress: 'bg-blue-100 text-blue-700',
      Stopped:    'bg-slate-100 text-slate-500',
      Unknown:    'bg-slate-100 text-slate-400',
    };
    return map[status] ?? 'bg-slate-100 text-slate-400';
  }
}
